import { useState, useRef, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useConversations } from '../hooks/useConversations';
import { useUser } from '../hooks/useUser';
import { truncateText } from '../utils';

function Sidebar({ version }) {
  const { convos, currentConvo, setCurrentConvo, newChat, requestDeleteConvo } = useConversations();
  const { isAdmin } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  const [showFullPath, setShowFullPath] = useState(false);
  const convosRef = useRef();

  const navigationConfig = {
    '/': {
      tabs: isAdmin ? [{ label: 'Manage Users', path: '/manage-users' }] : []
    },
    '/manage-users': {
      tabs: [{ label: 'Chat Home', path: '/' }]
    },
    '/training-sets': {
      tabs: [
        { label: 'Chat Home', path: '/' },
        { label: 'Manage Users', path: '/manage-users' }
      ]
    }
  };

  const currentNavigation = navigationConfig[location.pathname] || { tabs: [] };

  const handleDeleteConvo = (e, convoId, convoTitle) => {
    e.stopPropagation();
    requestDeleteConvo(convoId, convoTitle);
  };

  const handleNewChat = () => {
    if (location.pathname !== '/') {
      navigate('/');
    }
    newChat();
  };

  // Auto-scroll to active conversation
  useLayoutEffect(() => {
    if (convosRef.current && currentConvo) {
      const activeElement = convosRef.current.querySelector('.convo.active');
      if (activeElement) {
        activeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }
  }, [currentConvo]);

  const filteredConvos = convos.filter((convo) => convo.messages?.some(msg => msg.role === 'user'));

  return (
    <aside className="sidebar">
      <div className="sidebar-tabs">
        {currentNavigation.tabs.map((tab) => (
          <button
            key={tab.path}
            className="sidebar-tab"
            onClick={() => navigate(tab.path)}
          >
            {tab.label}
          </button>
        ))}
        <button className="sidebar-tab" onClick={handleNewChat}>
          New Chat
        </button>
      </div>

      {isAdmin && location.pathname !== '/training-sets' && (
        <div className="convos">
          <h4 className="section-heading">Training Sets</h4>
          <button className="sidebar-tab" onClick={() => navigate('/training-sets')}>
            Approve Labels
          </button>
        </div>
      )}

      <div className="convos" ref={convosRef}>
        {filteredConvos.length > 0 && (
          <h4 className="section-heading">Chats</h4>
        )}
        {filteredConvos.map((convo) => (
          <div
            key={convo.id}
            className={`convo ${currentConvo === convo.id ? 'active' : ''}`}
            onClick={() => {
              if (location.pathname !== '/') {
                navigate('/');
              }
              setCurrentConvo(convo.id);
            }}
          >
            <div className="convo-content">
              <div style={{ fontWeight: 500, fontSize: '13px' }}>
                {truncateText(convo.title)}
              </div>
              <button
                className="convo-delete"
                onClick={(e) => handleDeleteConvo(e, convo.id, convo.title)}
                aria-label={`Delete conversation ${convo.title}`}
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="muted" style={{ marginTop: 'auto', overflow: 'hidden' }}>
        {version ? (
          <>
            <div
              onDoubleClick={() => setShowFullPath(!showFullPath)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setShowFullPath(!showFullPath)
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`Toggle model path display. Currently showing ${showFullPath ? 'full path' : 'short name'}`}
              style={{ userSelect: 'none' }}
            >
              <b>{showFullPath ? version.hf_arch : version.hf_arch?.split('/')?.filter(Boolean)?.pop() || version.hf_arch}</b>
            </div>
            {showFullPath && (
              <div>Labels: {version.labels?.join(', ') || 'None'}</div>
            )}
          </>
        ) : (
          'Fetching version…'
        )}
      </div>
    </aside>
  );
}

export default Sidebar;